import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Paperclip, X } from 'lucide-react';
import { createTicket, uploadAttachment } from '@/api/support';
import { getErrorLogs, getErrorLogCount } from '@/lib/error-log-collector';
import { toUserMessage } from '@/lib/errors';
import { Button } from '@/components/assist/ui/button';
import { Input } from '@/components/assist/ui/input';
import { useSupportDrawer } from '@/contexts/SupportDrawerContext';
import { ScreenshotCapture } from './ScreenshotCapture';

export function TicketCreateForm() {
  const { t } = useTranslation('assist');
  const { setView } = useSupportDrawer();
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [includeErrorLogs, setIncludeErrorLogs] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [errorLogCount, setErrorLogCount] = useState(getErrorLogCount());

  useEffect(() => {
    const interval = setInterval(() => {
      setErrorLogCount(getErrorLogCount());
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const errorLogs = includeErrorLogs ? getErrorLogs() : null;
      const ticket = await createTicket({ summary, description, error_logs: errorLogs });

      const allFiles = [...attachments];
      if (screenshot) allFiles.push(screenshot);

      for (const file of allFiles) {
        await uploadAttachment(ticket.key, file);
      }

      setView('list');
    } catch (e) {
      setError(toUserMessage(e, t, { context: 'createTicket' }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col h-full">
      <button
        onClick={() => setView('list')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('support.create.back', 'Back to tickets')}
      </button>

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t('support.create.summary', 'Title')}
            </label>
            <Input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t('support.create.summaryPlaceholder', 'Brief description of the issue')}
              required
              maxLength={255}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t('support.create.description', 'Description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('support.create.descriptionPlaceholder', 'Describe the issue in detail...')}
              required
              rows={6}
              className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-1.5 block">
              {t('support.create.screenshot', 'Screenshot')}
            </label>
            <div className="space-y-2">
              <ScreenshotCapture
                onCapture={setScreenshot}
                screenshot={screenshot}
                onClear={() => setScreenshot(null)}
              />
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <input type="file" className="hidden" multiple onChange={handleFileSelect} accept="image/*,.pdf,.txt,.log" />
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                    <Paperclip className="h-4 w-4" />
                    {t('support.create.attachFile', 'Attach file')}
                  </span>
                </label>
              </div>
              {attachments.length > 0 && (
                <div className="space-y-1">
                  {attachments.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground bg-muted rounded-md px-2 py-1.5">
                      <Paperclip className="h-3 w-3 shrink-0" />
                      <span className="truncate flex-1">{f.name}</span>
                      <button type="button" onClick={() => removeAttachment(i)} className="shrink-0 hover:text-foreground transition-colors">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {errorLogCount > 0 && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeErrorLogs}
                onChange={(e) => setIncludeErrorLogs(e.target.checked)}
                className="rounded border-input"
              />
              <span className="text-sm">
                {t('support.create.includeErrorLogs', 'Include error logs for debugging')}
                <span className="text-muted-foreground ml-1">({errorLogCount})</span>
              </span>
            </label>
          )}
        </div>

        {error && <p className="text-sm text-destructive pt-3">{error}</p>}

        <div className="border-t border-border pt-4 mt-4 shrink-0">
          <Button type="submit" className="w-full" disabled={submitting || !summary || !description}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('support.create.submit', 'Submit Ticket')}
          </Button>
        </div>
      </form>
    </div>
  );
}
